import { useQuery } from "@tanstack/react-query";

import { apiGetV2 } from "~/shared/api/client";
import { GENERATION_CHANNELS, isImageInputRole } from "~/shared/data/chat-models";
import type {
    ArtifactType,
    AudioSupport,
    ChannelRequirement,
    GenerationChannel,
    ImageInputRole,
    ModeConfig,
    ModelBrand,
    ModelConfig,
} from "~/shared/data/chat-models";
import { toGenerationMode } from "~/shared/lib/generation-mode";

interface ApiModeConfig {
    name: string;
    title: string;
    description: string;
    aspectRatios: string[];
    resolutions: number[];
    durations: number[];
    upscaleFactors: number[];
    grokImagineModes: string[];
    audioSupport: AudioSupport;
    defaultAspectRatio: string | null;
    defaultResolution: number | null;
    defaultDuration: number | null;
    defaultUpscaleFactor: number | null;
    defaultGrokImagineMode: string | null;
    characterOrientations?: string[];
    defaultCharacterOrientation?: string | null;
    features?: string[];
    providerFeatureSets?: string[][];
    // Значения каналов приезжают как есть: клиент камелизует КЛЮЧИ, поэтому
    // бэкенд отдаёт список пар, а не объект «канал → фичи» (иначе `frame_pair`
    // приехал бы как `framePair` и ни с чем не сопоставился).
    channelRequirements?: { channel: string; features: string[] }[];
    imageRoles?: string[];
    maxElements?: number | null;
    maxReferenceImages?: number | null;
    referenceImagesSupported?: boolean;
    maxInputImages: number | null;
    maxInputVideos: number | null;
    maxInputAudio: number | null;
    minInputImages: number;
    minVisualInputs?: number;
    maxPromptLength: number | null;
    supportsNegativePrompt: boolean;
    maxBatchSize?: number;
}

interface ApiModelConfig {
    id: string;
    name: string;
    brand: string;
    brandLabel?: string;
    artifactType: ArtifactType;
    modes: ApiModeConfig[];
}

interface SettingsListResponse {
    models: ApiModelConfig[];
}

// Роли значимы позиционно (`imageRoles[i]` ↔ `inputImageUrls[i]`), поэтому
// незнакомую роль нельзя просто отфильтровать — сместился бы порядок кадров.
// Такой режим честнее отдать плоским пулом, чем собрать payload наугад.
const toImageRoles = (roles: string[] | undefined): ImageInputRole[] => {
    if (!roles || roles.length === 0) return [];
    return roles.every(isImageInputRole) ? [...roles] : [];
};

// Незнакомый канал отбрасываем: гасить по требованию, которого фронт не умеет
// заполнить, значило бы запереть UI из-за поля, добавленного бэком вперёд.
// Знакомые каналы при этом продолжают работать.
const toChannelRequirements = (
    entries: { channel: string; features: string[] }[] | undefined,
): ChannelRequirement[] =>
    (entries ?? [])
        .filter((entry): entry is { channel: GenerationChannel; features: string[] } =>
            (GENERATION_CHANNELS as readonly string[]).includes(entry.channel),
        )
        .map((entry) => ({ channel: entry.channel, features: entry.features }));

const toModeConfig = (api: ApiModeConfig): ModeConfig => ({
    name: toGenerationMode(api.name),
    title: api.title,
    description: api.description,
    aspectRatios: api.aspectRatios,
    resolutions: api.resolutions,
    durations: api.durations,
    upscaleFactors: api.upscaleFactors,
    grokImagineModes: api.grokImagineModes,
    audioSupport: api.audioSupport,
    defaultAspectRatio: api.defaultAspectRatio,
    defaultResolution: api.defaultResolution,
    defaultDuration: api.defaultDuration,
    defaultUpscaleFactor: api.defaultUpscaleFactor,
    defaultGrokImagineMode: api.defaultGrokImagineMode,
    // Fallback на пусто для старого бэка без этих полей (аддитивный контракт).
    characterOrientations: api.characterOrientations ?? [],
    defaultCharacterOrientation: api.defaultCharacterOrientation ?? null,
    features: api.features ?? [],
    providerFeatureSets: api.providerFeatureSets ?? [],
    channelRequirements: toChannelRequirements(api.channelRequirements),
    imageRoles: toImageRoles(api.imageRoles),
    maxElements: api.maxElements ?? null,
    maxReferenceImages: api.maxReferenceImages ?? null,
    referenceImagesSupported: api.referenceImagesSupported ?? false,
    maxInputImages: api.maxInputImages,
    maxInputVideos: api.maxInputVideos,
    maxInputAudio: api.maxInputAudio,
    minInputImages: api.minInputImages ?? 0,
    minVisualInputs: api.minVisualInputs ?? 0,
    maxPromptLength: api.maxPromptLength,
    supportsNegativePrompt: api.supportsNegativePrompt,
    // Поля нет (старый бэк) — считаем, что пакета режим не даёт: обещать
    // плитки, которых никто не исполнит, дороже, чем показать их закрытыми.
    maxBatchSize: api.maxBatchSize ?? 1,
});

const toModelConfig = (api: ApiModelConfig): ModelConfig => ({
    id: api.id,
    name: api.name,
    brand: api.brand as ModelBrand,
    // Лейбл бренда приходит с бэка; fallback на сырой enum-ключ, если поле
    // ещё не отдаётся (старый бэк) — честный сигнал вместо своей карты.
    brandLabel: api.brandLabel ?? api.brand,
    artifactType: api.artifactType,
    modes: api.modes.map(toModeConfig),
});

export const generationSettingsKey = ["v2", "generation", "settings"] as const;

export function fetchGenerationSettings(): Promise<ModelConfig[]> {
    return apiGetV2<SettingsListResponse>("/generation/settings").then((r) => r.models.map(toModelConfig));
}

export function useGenerationSettings() {
    return useQuery<ModelConfig[]>({
        queryKey: generationSettingsKey,
        queryFn: fetchGenerationSettings,
        staleTime: 5 * 60_000,
    });
}
